import React from "react";
import { Toaster } from "@/components/ui/toaster";
// import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { UnifiedAuthProvider } from "@/contexts/UnifiedAuthContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { YandexMetrikaProvider } from "@/contexts/YandexMetrikaContext";
import { NavigationBlockerProvider } from "@/contexts/NavigationBlockerContext";
import { GrafanaFaroProvider, FaroErrorBoundary } from "@/contexts/GrafanaFaroContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { RealtimeUpdates } from "./components/common/RealtimeUpdates";
import { YandexMetrikaTracker } from "./components/common/YandexMetrikaTracker";
import { GrafanaFaroTracker } from "./components/common/GrafanaFaroTracker";
import { FaroUserTracker } from "./components/grafana/FaroUserTracker";
import { OpenPanelTracker } from "./components/common/OpenPanelTracker";
import { OpenReplayTracker } from "./components/common/OpenReplayTracker";
import { captureAttributionFromUrl } from "@/lib/attribution";

// Захватываем attribution key из URL до рендеринга React
captureAttributionFromUrl();

// Pages
import Index from "./pages/Index";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import RecordDetails from "./pages/RecordDetails";
import Templates from "./pages/Templates";
import TemplateEditor from "@/components/templates/TemplateEditor";
import Balance from "./pages/Balance";
import Profile from "./pages/Profile";
import NotFound from "./pages/NotFound";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import RegistrationConfirmation from "@/pages/RegistrationConfirmation";
import PaymentResult from "@/pages/PaymentResult";
import LegalInfo from "@/pages/LegalInfo";
import Support from "@/pages/Support";
import UserGuide from "@/pages/UserGuide";
import EmailConfirmation from "@/pages/EmailConfirmation";
import SharedRecord from "@/pages/SharedRecord";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
      retry: 1,
      staleTime: 1000 * 60 * 5, // 5 минут
      // Добавляем структурное разделение для оптимизации обновлений
      structuralSharing: true,
      // Отключаем автоматическое обновление для real-time данных
      refetchInterval: false,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <GrafanaFaroProvider>
        <UnifiedAuthProvider>
          <ThemeProvider>
            <NotificationProvider>
              <NavigationBlockerProvider>
                <YandexMetrikaProvider
                  counterId={parseInt(
                    import.meta.env.VITE_YANDEX_METRIKA_ID || "0"
                  )}
                >
                <FaroErrorBoundary>
                  <TooltipProvider>
                      {/* User ID tracking for Grafana Faro */}
                      <FaroUserTracker />

                      {/* Компонент для отслеживания обновлений в реальном времени */}
                      <RealtimeUpdates />

                      {/* Компонент для отслеживания переходов в Яндекс.Метрике */}
                      <YandexMetrikaTracker />

                      {/* Компонент для отслеживания переходов в Grafana Faro */}
                      <GrafanaFaroTracker />

                      {/* Компонент для отслеживания переходов в OpenPanel */}
                      <OpenPanelTracker />

                      {/* Компонент для записи сессий в OpenReplay */}
                      <OpenReplayTracker />

                  <Routes>
                    <Route
                      path="/"
                      element={
                        <ProtectedRoute>
                          <Index />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route
                      path="/registration-confirmation"
                      element={<RegistrationConfirmation />}
                    />
                    <Route
                      path="/forgot-password"
                      element={<ForgotPassword />}
                    />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route
                      path="/email-confirmation"
                      element={<EmailConfirmation />}
                    />
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/record/:id"
                      element={
                        <ProtectedRoute>
                          <RecordDetails />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/templates"
                      element={
                        <ProtectedRoute>
                          <Templates />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/templates/create"
                      element={
                        <ProtectedRoute>
                          <TemplateEditor mode="create" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/templates/edit/:id"
                      element={
                        <ProtectedRoute>
                          <TemplateEditor mode="edit" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/balance"
                      element={
                        <ProtectedRoute>
                          <Balance />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <Profile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/support"
                      element={
                        <ProtectedRoute>
                          <Support />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/guide"
                      element={
                        <ProtectedRoute>
                          <UserGuide />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route
                      path="/legal"
                      element={
                        <ProtectedRoute>
                          <LegalInfo />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/payment/result"
                      element={
                        <ProtectedRoute>
                          <PaymentResult />
                        </ProtectedRoute>
                      }
                    />
                    {/* Публичный доступ к shared записям - БЕЗ ProtectedRoute */}
                    <Route path="/shared/:token" element={<SharedRecord />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  <Toaster />
                  {/* <Sonner /> */}
                  </TooltipProvider>
                </FaroErrorBoundary>
              </YandexMetrikaProvider>
            </NavigationBlockerProvider>
          </NotificationProvider>
        </ThemeProvider>
        </UnifiedAuthProvider>
      </GrafanaFaroProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
